import React, { useEffect, useMemo } from "react";
import { Table, Button, Form, InputNumber, Select, Tooltip, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

import SingleSelector from "./SingleSelector";
import { validateMinLessThanMaxFactory } from "../utils/validators";

export default function DeviceModalTabThresholds({ mode, device, form, elements, periodicities }) {
  const draftThresholds = Form.useWatch("thresholdsDraft", form) || [];
  const elementPeriodicities = form.getFieldValue("elementPeriodicities") || {};
  const allowedElementIds = Object.keys(elementPeriodicities).map(Number);

  const elementsForSelector = useMemo(() => {
    if (!allowedElementIds.length) return [];
    return elements
      .filter((e) => allowedElementIds.includes(e.id))
      .map((e) => ({
        ...e,
        labelWithUnit: e.unit ? `${e.name} (${e.unit})` : e.name,
      }));
  }, [elements, allowedElementIds]);

  const selectedElementId = Form.useWatch("elementId", form);

  const allowedPeriodicityIds = useMemo(() => {
    if (!selectedElementId) return [];
    return elementPeriodicities[String(selectedElementId)] || [];
  }, [elementPeriodicities, selectedElementId]);

  const periodicityOptions = useMemo(
    () =>
      periodicities
        .filter((p) => allowedPeriodicityIds.includes(p.id))
        .map((p) => ({ label: p.name, value: p.id })),
    [periodicities, allowedPeriodicityIds]
  );

  useEffect(() => {
    const currentPeriodicity = form.getFieldValue("periodicityId");
    if (!selectedElementId || !currentPeriodicity) return;

    if (!allowedPeriodicityIds.includes(currentPeriodicity)) {
      form.setFieldsValue({ periodicityId: undefined });
    }
  }, [selectedElementId, allowedPeriodicityIds, form]);

  const wElementId = Form.useWatch("elementId", form);
  const wPeriodicityId = Form.useWatch("periodicityId", form);
  const wMin = Form.useWatch("minValue", form);
  const wMax = Form.useWatch("maxValue", form);

  const canSubmit =
    !!wElementId &&
    !!wPeriodicityId &&
    (wMin != null || wMax != null);

  const handleAdd = async () => {
    const values = await form.validateFields([
      "elementId",
      "periodicityId",
      "minValue",
      "maxValue",
    ]);

    const allowed = elementPeriodicities[values.elementId] || [];
    if (!allowed.includes(values.periodicityId)) {
      message.error("This element and aggregation are not configured.");
      return;
    }

    const exists = draftThresholds.some(
      (t) => t.elementId === values.elementId && t.periodicityId === values.periodicityId
    );

    if (exists) {
      message.warning("A threshold for this element and aggregation already exists.");
      return;
    }

    form.setFieldsValue({
      thresholdsDraft: sortThresholdsBySelectorOrder([
        ...draftThresholds,
        values,
      ]),
    });

    form.setFieldsValue({
      elementId: undefined,
      periodicityId: undefined,
      minValue: null,
      maxValue: null,
    });
  };

  const handleDelete = (record) => {
    form.setFieldsValue({
      thresholdsDraft: draftThresholds.filter((t) => t !== record),
    });
  };

  const elementOrderMap = useMemo(() => {
    const map = {};
    elementsForSelector.forEach((el, index) => {
      map[el.id] = index;
    });
    return map;
  }, [elementsForSelector]);

  const periodicityOrderMap = useMemo(() => {
    const map = {};
    periodicities.forEach((p, index) => {
      map[p.id] = index;
    });
    return map;
  }, [periodicities]);

  const sortThresholdsBySelectorOrder = (list) =>
    [...list].sort((a, b) => {
      const elOrderA = elementOrderMap[a.elementId] ?? Number.MAX_SAFE_INTEGER;
      const elOrderB = elementOrderMap[b.elementId] ?? Number.MAX_SAFE_INTEGER;

      if (elOrderA !== elOrderB) {
        return elOrderA - elOrderB;
      }

      const perOrderA = periodicityOrderMap[a.periodicityId] ?? Number.MAX_SAFE_INTEGER;
      const perOrderB = periodicityOrderMap[b.periodicityId] ?? Number.MAX_SAFE_INTEGER;

      return perOrderA - perOrderB;
  });

  const columns = [
    {
      title: "Element",
      dataIndex: "elementId",
      key: "elementId",
      width: 220,
      render: (id) => {
        const el = elements.find((e) => e.id === id);
        if (!el) return id;
        return el.unit ? `${el.name} (${el.unit})` : el.name;
      },
    },
    {
      title: "Aggregation",
      dataIndex: "periodicityId",
      key: "periodicityId",
      width: 140,
      render: (id) => periodicities.find((p) => p.id === id)?.name || id,
    },
    { title: "Min", dataIndex: "minValue", key: "minValue", width: 80, },
    { title: "Max", dataIndex: "maxValue", key: "maxValue", width: 80, },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDelete(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      {/* ACTION BAR */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 16, flex: 1 }}
        >
          <Form.Item name="thresholdsDraft" hidden>
            <input />
          </Form.Item>
          <Form.Item
            name="elementId"
            rules={[{ required: true, message: "Element is required" }]}
            style={{ width: 220, marginBottom: 0 }}
          >
            <SingleSelector
              key={allowedElementIds.join(",")}
              items={elementsForSelector}
              placeholder={
                allowedElementIds.length ? "Select element" : "Configure elements first"
              }
              idKey="id"
              labelKey="labelWithUnit"
              width="100%"
              disabled={allowedElementIds.length === 0}
            />
          </Form.Item>

          <Form.Item
            name="periodicityId"
            rules={[{ required: true, message: "aggregation is required" }]}
            style={{ width: 175, marginBottom: 0 }}
          >
            <Select
              placeholder={selectedElementId ? "Select aggregation" : "Select element first"}
              options={periodicityOptions}
              disabled={!selectedElementId}
              getPopupContainer={(node) => node.parentNode}
            />
          </Form.Item>

          <Form.Item 
            name="minValue" 
            style={{ width: 80, marginBottom: 0 }}>
            <InputNumber placeholder="Min" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="maxValue"
            style={{ width: 80, marginBottom: 0 }}
            rules={[{ validator: validateMinLessThanMaxFactory(form) }]}
          >
            <InputNumber placeholder="Max" style={{ width: "100%" }} />
          </Form.Item>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={!canSubmit}
            style={{ marginLeft: "auto" }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* TABLE */}
      <Table
        rowKey={(r) => `${r.elementId}-${r.periodicityId}`}
        dataSource={draftThresholds}
        columns={columns}
        loading={false}
        pagination={false}
        scroll={{ y: 240 }} 
        locale={{
          emptyText: (
            <div style={{ padding: 32 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                No thresholds configured
              </div>
              <div style={{ color: "#999" }}>
                Use the form above to add the first threshold.
              </div>
            </div>
          ),
        }}
      />
    </>
  );
}
